<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import InternalChatChannelsAPI from 'dashboard/api/internalChatChannels';

const props = defineProps({
  channelId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['updated']);

const store = useStore();
const { t } = useI18n();

const isOpen = ref(false);
const addMode = ref('all');
const autoAddNewMembers = ref(true);
const selectedAgentIds = ref([]);
const searchQuery = ref('');
const isAdding = ref(false);
const existingMemberIds = ref(new Set());
const isLoadingMembers = ref(false);

const channel = computed(() =>
  store.getters['internalChat/getChannelById'](props.channelId)
);

const channelName = computed(() => channel.value?.name || '');

const allAgents = computed(() => store.getters['agents/getAgents'] || []);

const agentCount = computed(() => allAgents.value.length);

const availableAgents = computed(() =>
  allAgents.value.filter(a => !existingMemberIds.value.has(a.id))
);

const filteredAvailableAgents = computed(() => {
  if (!searchQuery.value) return availableAgents.value;
  const query = searchQuery.value.toLowerCase();
  return availableAgents.value.filter(a =>
    (a.name || '').toLowerCase().includes(query)
  );
});

const hasChanges = computed(() => {
  if (addMode.value === 'all' && availableAgents.value.length > 0) return true;
  if (addMode.value === 'specific' && selectedAgentIds.value.length > 0)
    return true;
  const currentAutoAdd = channel.value?.auto_add_new_members ?? false;
  return currentAutoAdd !== autoAddNewMembers.value;
});

function selectMode(mode) {
  addMode.value = mode;
  if (mode === 'all') {
    autoAddNewMembers.value = true;
    selectedAgentIds.value = [];
    searchQuery.value = '';
  } else {
    autoAddNewMembers.value = false;
  }
}

function toggleAgent(agentId) {
  const idx = selectedAgentIds.value.indexOf(agentId);
  if (idx === -1) {
    selectedAgentIds.value.push(agentId);
  } else {
    selectedAgentIds.value.splice(idx, 1);
  }
}

async function fetchMembers() {
  isLoadingMembers.value = true;
  try {
    const { data } = await InternalChatChannelsAPI.getMembers(props.channelId);
    existingMemberIds.value = new Set(data.map(m => m.user_id));
  } catch {
    // silently handle
  } finally {
    isLoadingMembers.value = false;
  }
}

async function handleAdd() {
  isAdding.value = true;
  try {
    const usersToAdd =
      addMode.value === 'all'
        ? availableAgents.value.map(a => a.id)
        : selectedAgentIds.value.filter(id => !existingMemberIds.value.has(id));

    for (const userId of usersToAdd) {
      await InternalChatChannelsAPI.addMember(props.channelId, userId);
    }

    const currentAutoAdd = channel.value?.auto_add_new_members ?? false;
    if (currentAutoAdd !== autoAddNewMembers.value) {
      await store.dispatch('internalChat/update', {
        channelId: props.channelId,
        channel: { auto_add_new_members: autoAddNewMembers.value },
      });
    }

    emit('updated');
    isOpen.value = false;
  } finally {
    isAdding.value = false;
  }
}

function close() {
  if (isAdding.value) return;
  isOpen.value = false;
}

function open() {
  addMode.value = 'all';
  autoAddNewMembers.value = channel.value?.auto_add_new_members ?? true;
  selectedAgentIds.value = [];
  searchQuery.value = '';
  store.dispatch('agents/get');
  fetchMembers();
  isOpen.value = true;
}

defineExpose({ open });
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isOpen"
        class="fixed inset-0 z-[200] flex items-center justify-center p-4"
      >
        <div
          class="absolute inset-0 bg-black/40 backdrop-blur-sm"
          @click="close"
        />

        <div class="relative z-10 w-full max-w-md rounded-xl bg-n-solid-2 shadow-xl">
          <!-- Header -->
          <div class="flex items-start justify-between border-b border-n-slate-4 px-6 pt-6 pb-4">
            <div>
              <h2 class="text-base font-semibold text-n-slate-12">
                {{ t('INTERNAL_CHAT.ADD_MEMBERS.TITLE') }}
              </h2>
              <p v-if="channelName" class="mt-0.5 text-sm text-n-slate-10">
                #{{ channelName }}
              </p>
            </div>
            <button
              class="flex-shrink-0 rounded-lg p-1.5 text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
              @click="close"
            >
              <Icon icon="i-lucide-x" class="size-4" />
            </button>
          </div>

          <!-- Body -->
          <div class="px-6 py-5">
            <div class="flex flex-col gap-3">
              <!-- Option 1: Add all -->
              <button
                type="button"
                class="flex items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors"
                :class="
                  addMode === 'all'
                    ? 'border-n-brand bg-n-blue-3'
                    : 'border-n-slate-6 hover:bg-n-alpha-1'
                "
                @click="selectMode('all')"
              >
                <div class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-n-alpha-1">
                  <i class="i-lucide-users text-base text-n-slate-11" />
                </div>
                <div class="flex-1">
                  <p class="text-sm font-medium text-n-slate-12">
                    {{ t('INTERNAL_CHAT.ADD_MEMBERS.ADD_ALL', { count: agentCount }) }}
                  </p>
                  <p class="text-xs text-n-slate-10">
                    {{ t('INTERNAL_CHAT.ADD_MEMBERS.ADD_ALL_DESC') }}
                  </p>
                </div>
                <span
                  class="flex size-4 flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors"
                  :class="addMode === 'all' ? 'border-n-brand bg-n-brand' : 'border-n-slate-6'"
                >
                  <span v-if="addMode === 'all'" class="size-1.5 rounded-full bg-white" />
                </span>
              </button>

              <!-- Option 2: Specific people -->
              <button
                type="button"
                class="flex items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors"
                :class="
                  addMode === 'specific'
                    ? 'border-n-brand bg-n-blue-3'
                    : 'border-n-slate-6 hover:bg-n-alpha-1'
                "
                @click="selectMode('specific')"
              >
                <div class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-n-alpha-1">
                  <i class="i-lucide-user-plus text-base text-n-slate-11" />
                </div>
                <div class="flex-1">
                  <p class="text-sm font-medium text-n-slate-12">
                    {{ t('INTERNAL_CHAT.ADD_MEMBERS.ADD_SPECIFIC') }}
                  </p>
                  <p class="text-xs text-n-slate-10">
                    {{ t('INTERNAL_CHAT.ADD_MEMBERS.ADD_SPECIFIC_DESC') }}
                  </p>
                </div>
                <span
                  class="flex size-4 flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors"
                  :class="addMode === 'specific' ? 'border-n-brand bg-n-brand' : 'border-n-slate-6'"
                >
                  <span v-if="addMode === 'specific'" class="size-1.5 rounded-full bg-white" />
                </span>
              </button>

              <!-- Agent picker (specific mode) -->
              <div v-if="addMode === 'specific'" class="flex flex-col gap-2">
                <input
                  v-model="searchQuery"
                  type="text"
                  class="w-full rounded-lg border border-n-slate-6 bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 placeholder-n-slate-10 outline-none transition-colors focus:border-n-brand"
                  :placeholder="t('INTERNAL_CHAT.DM.SEARCH_PLACEHOLDER')"
                />
                <div class="flex max-h-48 flex-col gap-0.5 overflow-y-auto rounded-lg border border-n-slate-6 p-1.5">
                  <button
                    v-for="agent in filteredAvailableAgents"
                    :key="agent.id"
                    type="button"
                    class="flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-n-alpha-1"
                    :class="selectedAgentIds.includes(agent.id) ? 'bg-n-blue-3' : ''"
                    @click="toggleAgent(agent.id)"
                  >
                    <Avatar
                      :name="agent.name || ''"
                      :src="agent.thumbnail || ''"
                      :size="24"
                      rounded-full
                    />
                    <span class="flex-1 truncate text-left text-n-slate-12">
                      {{ agent.name }}
                    </span>
                    <i
                      v-if="selectedAgentIds.includes(agent.id)"
                      class="i-lucide-check flex-shrink-0 text-sm text-n-brand"
                    />
                  </button>
                  <p
                    v-if="filteredAvailableAgents.length === 0"
                    class="px-2 py-4 text-center text-sm text-n-slate-10"
                  >
                    {{ t('INTERNAL_CHAT.DM.NO_AGENTS') }}
                  </p>
                </div>
              </div>

              <!-- Auto-add switch -->
              <label class="flex cursor-pointer items-start justify-between gap-4 rounded-lg border border-n-slate-5 px-4 py-3">
                <span class="flex flex-col gap-0.5">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ t('INTERNAL_CHAT.CHANNEL.AUTO_ADD_NEW_MEMBERS') }}
                  </span>
                  <span class="text-xs text-n-slate-10">
                    {{ t('INTERNAL_CHAT.CHANNEL.AUTO_ADD_NEW_MEMBERS_DESC') }}
                  </span>
                </span>
                <Switch v-model="autoAddNewMembers" class="mt-0.5 flex-shrink-0" />
              </label>
            </div>
          </div>

          <!-- Footer -->
          <div class="flex items-center justify-end border-t border-n-slate-4 px-6 py-4">
            <button
              class="rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 active:opacity-80 disabled:cursor-not-allowed disabled:opacity-40"
              :disabled="isAdding || !hasChanges"
              @click="handleAdd"
            >
              <span v-if="isAdding" class="flex items-center gap-2">
                <i class="i-lucide-loader-circle animate-spin text-sm" />
                {{ t('INTERNAL_CHAT.ADD_MEMBERS.ADDING') }}
              </span>
              <span v-else>{{ t('INTERNAL_CHAT.ADD_MEMBERS.CONFIRM') }}</span>
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
